import { css } from 'styled-components';

import { controllerTokens } from '../../../tokens/components/Controller/tokens';

// Controller default border radius
const borderRadius = controllerTokens.ContainerBorderRadiusBaseDefault;

// Horizontal measures (pixels)
const inlineGutter = 6;
const fieldControlWidth = 300;
const halfWidth = (fieldControlWidth - inlineGutter) / 2;
const quarterWidth = (fieldControlWidth - inlineGutter * 3) / 4;

// Still used by old Table
const borderColors = {
  default: controllerTokens.ContainerBorderColorBaseDefault,
  hover: controllerTokens.ContainerBorderColorBaseHover,
  focus: controllerTokens.ContainerBorderColorBaseKeyboardFocus,
  error: controllerTokens.ContainerBorderColorInvalidDefault,
  errorFocus: controllerTokens.ContainerBorderColorInvalidDefault,
  disabled: controllerTokens.ContainerBorderColorDisabledDefault,
};

// Still used by old Table
const borders = {
  default: `1px solid ${borderColors.default}`,
  hover: `1px solid ${borderColors.hover}`,
  focus: `2px solid ${borderColors.focus}`,
  error: `1px solid ${borderColors.error}`,
  errorFocus: `2px solid ${borderColors.error}`,
  disabled: `1px solid ${borderColors.disabled}`,
};

const inputWidths = {
  large: `${fieldControlWidth}px`,
  half: `${halfWidth}px`,
  quarter: `${quarterWidth}px`,
  full: `100%`,
  datePicker: `160px`,
};

const base = css`
  background-color: ${controllerTokens.ContainerBackgroundColorBaseDefault};
  border-color: ${controllerTokens.ContainerBorderColorBaseDefault};
  border-radius: ${controllerTokens.ContainerBorderRadiusBaseDefault};
  border-style: ${controllerTokens.ContainerBorderStyleBaseDefault};
  border-width: ${controllerTokens.ContainerBorderWidthBaseDefault};
  color: inherit;
  min-width: 0;
  outline: none;
  overflow: hidden; // Prevent it to overflow from its flex wrapper when using affixes
  transition: border 80ms ease-out, box-shadow 80ms ease-out;
  vertical-align: baseline;

  &::placeholder {
    color: ${controllerTokens.ContainerFontColorPlaceholderDefault};
  }

  &:hover {
    border-color: ${controllerTokens.ContainerBorderColorBaseHover};
  }

  /*
    Focus & Background bg to override legacy css in CW.
  */
  &:focus-visible {
    background-color: ${controllerTokens.ContainerBackgroundColorBaseDefault};
    border-color: ${controllerTokens.ContainerBorderColorBaseKeyboardFocus};
    box-shadow: 0 0 0 ${controllerTokens.ContainerShadowWidthBaseKeyboardFocus}
      ${controllerTokens.ContainerShadowColorBaseKeyboardFocus};
  }

  ${({ theme }) =>
    theme.controllerInvalid &&
    !theme.controllerDisabled &&
    css`
      border-color: ${controllerTokens.ContainerBorderColorInvalidDefault};

      &:hover,
      &:focus-visible {
        border-color: ${controllerTokens.ContainerBorderColorInvalidDefault};
      }

      &:focus-visible {
        box-shadow: 0 0 0 ${controllerTokens.ContainerShadowWidthInvalidKeyboardFocus}
          ${controllerTokens.ContainerShadowColorInvalidKeyboardFocus};
      }
    `}

  ${({ theme }) =>
    theme.controllerDisabled &&
    css`
      cursor: not-allowed;
      background-color: ${controllerTokens.ContainerBackgroundColorDisabledDefault};
      border-color: ${controllerTokens.ContainerBorderColorDisabledDefault};
      color: ${controllerTokens.ContainerFontColorDisabledDefault};
      -webkit-text-fill-color: ${controllerTokens.ContainerFontColorDisabledDefault};

      &:hover,
      &:focus-visible {
        border-color: ${controllerTokens.ContainerBorderColorDisabledDefault};
      }
    `}
`;

const hideInput = css`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
`;

const textEntry = css`
  height: 42px;
  padding: 10px;
  text-align: ${props => props.theme.controllerAlign};
  width: ${props => inputWidths[props.theme.controllerWidth] || inputWidths.large};
  font-family: ${controllerTokens.ContainerFontFamilyBaseDefault};
  font-feature-settings: ${controllerTokens.InputFontFeatureBase};
  font-size: ${controllerTokens.ContainerFontSizeBaseDefault};
  font-variant-numeric: inherit;
  line-height: 20px;

  ${({ theme }) =>
    theme.prefix &&
    css`
      border-bottom-left-radius: 0;
      border-top-left-radius: 0;
    `}

  ${({ theme }) =>
    theme.suffix &&
    css`
      border-bottom-right-radius: 0;
      border-top-right-radius: 0;

      &:focus-visible {
        z-index: 1;
      }
    `}
`;

const numericEntry = css`
  font-variant-numeric: tabular-nums;
`;

const appearanceEntry = css`
  border-radius: ${controllerTokens.ContainerBorderRadiusBaseDefault};
  color: ${controllerTokens.IconColorBaseDefault};
  cursor: pointer;
  display: inline-flex;
  height: 22px;
  justify-content: center;
  min-width: 22px;
  width: 22px;
  user-select: none;

  input:focus-visible ~ && {
    border-color: ${controllerTokens.ContainerBorderColorBaseKeyboardFocus};
    box-shadow: 0 0 0 ${controllerTokens.ContainerShadowWidthBaseKeyboardFocus}
      ${controllerTokens.ContainerShadowColorBaseKeyboardFocus};
  }

  ${({ theme }) =>
    theme.controllerInvalid &&
    css`
      input:focus-visible ~ && {
        border-color: ${controllerTokens.ContainerBorderColorInvalidDefault};
        box-shadow: 0 0 0 ${controllerTokens.ContainerShadowWidthBaseKeyboardFocus}
          ${controllerTokens.ContainerShadowColorInvalidKeyboardFocus};
      }
    `};

  ${({ theme }) =>
    theme.controllerDisabled &&
    css`
      color: ${controllerTokens.IconColorBaseDisabled};
      cursor: not-allowed;
    `}
`;

const appearanceLabel = css`
  font-size: ${controllerTokens.ContainerFontSizeBaseDefault};
  line-height: ${controllerTokens.ContainerFontLeadingBaseDefault};
  ${({ theme }) =>
    theme.fieldIsAgreement
      ? css`
          font-weight: ${controllerTokens.ContainerFontWeightAgreementDefault};
        `
      : css`
          font-weight: ${controllerTokens.ContainerFontWeightBaseDefault};
        `}
  margin-left: 8px;
  user-select: none;
`;

export {
  appearanceEntry,
  appearanceLabel,
  base,
  borderColors,
  borderRadius,
  borders,
  fieldControlWidth,
  hideInput,
  inlineGutter,
  inputWidths,
  textEntry,
  numericEntry,
};
